import clsx from 'clsx';
import React from 'react';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { SecondaryButtonV2 } from '@fragment/ui/src/components/Button/SecondaryButtonV2/SecondaryButtonV2';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';

import type { OnboardingState } from './onboardingReducer';
import {
  OnboardingChecklist,
  OnboardingDropdown,
  OnboardingTextArea,
  OnboardingToggle,
} from './SharedUI';

const INDUSTRY_VERTICALS = [
  {
    value: 'office_supplies',
    label: 'Office supplies & equipment',
  },
  {
    value: 'saas',
    label: 'SaaS / software / cloud services',
  },
  {
    value: 'industrial',
    label: 'Industrial equipment & machinery',
  },
  {
    value: 'food',
    label: 'Food service & restaurant supply',
  },
  {
    value: 'professional',
    label: 'Professional services (legal, accounting, consulting)',
  },
  {
    value: 'janitorial',
    label: 'Janitorial & facilities maintenance supplies',
  },
  {
    value: 'medical_supply',
    label: 'Medical / dental supplies (non-pharmaceutical)',
  },
  {
    value: 'building',
    label: 'Building materials & construction supplies',
  },
  {
    value: 'packaging',
    label: 'Packaging & shipping supplies',
  },
  {
    value: 'agricultural',
    label: 'Agricultural products & farm supplies',
  },
  {
    value: 'auto_parts',
    label: 'Automotive parts & fleet supplies',
  },
  {
    value: 'textiles',
    label: 'Textiles & apparel (wholesale)',
  },
  {
    value: 'printing',
    label: 'Printing & marketing materials',
  },
  {
    value: 'hardware',
    label: 'IT hardware & networking equipment',
  },
  {
    value: 'pet_supply',
    label: 'Pet supplies (wholesale)',
  },
  {
    value: 'jewels',
    label: 'Precious metals, stones, or jewelry',
  },
  {
    value: 'collectibles',
    label: 'Art, antiques, or high-value collectibles',
  },
  {
    value: 'weapons',
    label: 'Weapons, firearms, ammunition, or defense equipment',
  },
  {
    value: 'pharma',
    label: 'Pharmaceuticals or controlled substances',
  },
  {
    value: 'cannabis',
    label: 'Cannabis, CBD, or hemp-derived products',
  },
  {
    value: 'crypto',
    label: 'Cryptocurrency or digital assets',
  },
  {
    value: 'money_orders',
    label: 'Money orders, check cashing, or currency exchange services',
  },
  {
    value: 'adult_content',
    label: 'Adult content or products',
  },
  {
    value: 'gambling',
    label: 'Gambling or gaming equipment',
  },
  {
    value: 'tobacco',
    label: 'Tobacco or vaping products',
  },
  {
    value: 'corporate_services',
    label: 'Corporate formation or registered agent services',
  },
  {
    value: 'exports',
    label: 'Export-controlled goods',
  },
  {
    value: 'resale_electronics',
    label: 'High-resale electronics (smartphones, GPUs, gaming consoles)',
  },
  {
    value: 'gift_cards',
    label: 'Gift cards, prepaid cards, or stored-value products',
  },
  {
    value: 'luxury',
    label: 'Luxury goods (designer handbags, watches, high-end fashion)',
  },
];

const BUSINESS_LOCATIONS = [
  { value: 'usa', label: 'United States' },
  { value: 'canada', label: 'Canada' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'eu', label: 'EU / EEA member states' },
  { value: 'aus_nz', label: 'Australia / New Zealand' },
  { value: 'jp_sk_sp', label: 'Japan / South Korea / Singapore' },
  { value: 'turkey', label: 'Turkey' },
  { value: 's_africa', label: 'South Africa' },
  { value: 'nigeria', label: 'Nigeria' },
  { value: 'vietnam', label: 'Vietnam' },
  { value: 'iran', label: 'Iran' },
  { value: 'north_korea', label: 'North Korea' },
  { value: 'myanmar', label: 'Myanmar' },
  { value: 'cuba', label: 'Cuba' },
  { value: 'syria', label: 'Syria' },
  { value: 'russia', label: 'Russia' },
  { value: 'belarus', label: 'Belarus' },
  { value: 'venezuela', label: 'Venezuela' },
  { value: 'bvi', label: 'British Virgin Islands' },
  { value: 'cayman', label: 'Cayman Islands' },
  { value: 'seychelles', label: 'Seychelles' },
  { value: 'panama', label: 'Panama' },
];

type BusinessViewProps = {
  onboardingState: OnboardingState;
  setOnboardingField: (
    stateKey: string,
    value: string | string[] | boolean | { type: string; number: string }[]
  ) => void;
  onNext?: () => void;
};

const AddButton = ({ onClick }: { onClick: () => void }) => (
  <SecondaryButtonV2 onClick={onClick} className="text-left">
    Add+
  </SecondaryButtonV2>
);

const BusinessDiagramColumn = ({
  title,
  children,
  onAdd,
}: {
  title: string;
  children?: React.ReactNode;
  onAdd?: () => void;
}) => (
  <div className="flex flex-col border-[0.5px] border-main-300">
    <span
      className={clsx(
        'text-left text-main-500 pb-f1 bg-canvas',
        children && 'border-b-[0.5px] border-main-300'
      )}
    >
      {title}
    </span>
    {children}
    {onAdd && <AddButton onClick={onAdd} />}
  </div>
);

const BusinessDiagram = () => (
  <div className="flex flex-col">
    <span className="text-md text-main-500 min-w-f21">Business Diagram</span>
    <div className="flex flex-col text-main border-[0.5px] border-main-300 bg-negative p-f1 gap-y-f2">
      <span>
        Buyers are end users who will pay into invoices generated by Fragment
        Billing.
      </span>
      <span>
        Sellers are end users who will receive payouts for products or services
        in an Invoice.
      </span>
      <span>
        The platform is the business providing a market place to connect these
        groups.
      </span>
      {/* 5 columns: Buyers | arrow | Invoice+Platform | arrow | Sellers */}
      <div
        className="grid grid-cols-5 gap-x-f0 items-start"
        style={{ gridTemplateColumns: '1fr auto 1fr auto 1fr' }}
      >
        <BusinessDiagramColumn title="Buyers" onAdd={() => {}}>
          <span className="text-main">Employers</span>
        </BusinessDiagramColumn>
        <span className="w-f2 text-center text-main-500">→</span>
        <div className="flex flex-col gap-y-f0">
          <BusinessDiagramColumn title="Invoice" />
          <span className="w-full text-center text-main-500">↓</span>
          <BusinessDiagramColumn title="Platform" onAdd={() => {}}>
            <span className="text-main">Commission</span>
          </BusinessDiagramColumn>
        </div>
        <span className="w-f2 text-center text-main-500">→</span>
        <BusinessDiagramColumn title="Sellers" onAdd={() => {}}>
          <span className="text-main">Carriers</span>
          <span className="text-main">Agencies</span>
          <span className="text-main">Brokers</span>
        </BusinessDiagramColumn>
      </div>
    </div>
  </div>
);

type RegulatedBusinessInput = {
  licenseType: string;
  licenseNumber: string;
};

const RegulatedBusinessInputField = ({
  details,
  setField,
}: {
  details: RegulatedBusinessInput;
  setField: (field: 'licenseType' | 'licenseNumber', value: string) => void;
}) => (
  <div className="flex flex-row gap-x-[2ch]">
    <TextInput
      label="Regulator"
      flexContainer="flex-col"
      value={details.licenseType}
      onChange={(e) => setField('licenseType', e.target.value)}
      padding="p-f0"
      height="h-f2"
      size="md"
      width="w-full"
    />
    <TextInput
      label="License Number"
      flexContainer="flex-col"
      value={details.licenseNumber}
      onChange={(e) => setField('licenseNumber', e.target.value)}
      padding="p-f0"
      height="h-f2"
      size="md"
      width="w-full"
    />
  </div>
);

export const BusinessCDDView = ({
  onboardingState,
  setOnboardingField,
  onNext,
}: BusinessViewProps) => (
  <div className="flex flex-col h-fit gap-y-f2">
    <OnboardingTextArea
      stateKey="description"
      label="Describe the business's product"
      state={onboardingState}
      setField={setOnboardingField}
    />
    <OnboardingDropdown
      stateKey="industryVertical"
      label="What industry verticals does your marketplace primarily serve?"
      options={INDUSTRY_VERTICALS}
      state={onboardingState}
      setField={setOnboardingField}
    />
    <OnboardingDropdown
      stateKey="buyerSellerConnection"
      label="What is the typical pattern of connection between buyers and sellers?"
      options={[
        {
          value: 'same-sellers-consistently',
          label: 'Buyers work with the same sellers consistently',
        },
        {
          value: 'many-different-sellers',
          label: 'Buyers work with many different sellers',
        },
      ]}
      state={onboardingState}
      setField={setOnboardingField}
    />
    <OnboardingToggle
      label="Does your marketplace allow sellers to operate as intermediaries, resellers, or drop-shippers?"
      stateKey="sellersAsIntermediaries"
      state={onboardingState}
      setField={setOnboardingField}
    />
    <OnboardingToggle
      label="Does your marketplace facilitate cross-border transactions?"
      stateKey="allowsCrossBorder"
      state={onboardingState}
      setField={setOnboardingField}
    />
    {onboardingState.allowsCrossBorder && (
      <OnboardingChecklist
        stateKey="countriesOfOperation"
        selectedItems={onboardingState.countriesOfOperation ?? []}
        label="In which countries do your marketplace participants operate? (Select all that apply)"
        items={BUSINESS_LOCATIONS}
        setField={setOnboardingField}
      />
    )}

    <BusinessDiagram />
    <OnboardingTextArea
      stateKey="strategicPartners"
      label="List any partner businesses who will create or modify invoices and make payments."
      state={onboardingState}
      setField={setOnboardingField}
    />

    <OnboardingToggle
      stateKey="isRegulatedIndustry"
      label="Is the business part of a regulated industry?"
      state={onboardingState}
      setField={setOnboardingField}
    />
    {onboardingState.isRegulatedIndustry && (
      <>
        <div className="flex flex-col">
          <span className="text-main-500">
            Additional Regulatory Information
          </span>
          <div className="flex flex-col border-main-300 border-[0.5px] p-f1 gap-y-f2">
            <OnboardingToggle
              label="Does your company hold any financial licenses or registrations (e.g. money transmitter license, payment processor registration)?"
              stateKey="holdFinancialLicenses"
              state={onboardingState}
              setField={setOnboardingField}
            />
            {onboardingState.holdFinancialLicenses &&
              onboardingState.licenses?.map((license, index) => (
                <RegulatedBusinessInputField
                  key={`${license.type}-${license.number}`}
                  details={{
                    licenseType: license.type,
                    licenseNumber: license.number,
                  }}
                  setField={(field, value) => {
                    const nextLicenses = onboardingState.licenses?.map(
                      (
                        currentLicense,
                        currentIndex
                      ): { type: string; number: string } =>
                        currentIndex === index
                          ? {
                              ...currentLicense,
                              [field === 'licenseType' ? 'type' : 'number']:
                                value,
                            }
                          : currentLicense
                    );
                    setOnboardingField('licenses', nextLicenses ?? []);
                  }}
                />
              ))}
            {onboardingState.holdFinancialLicenses && (
              <AddButton
                onClick={() =>
                  setOnboardingField('licenses', [
                    ...(onboardingState.licenses ?? []),
                    { type: '', number: '' },
                  ])
                }
              />
            )}
          </div>
        </div>
      </>
    )}
    {onNext != null && <PrimaryButtonV2 onClick={onNext}>Next</PrimaryButtonV2>}
  </div>
);
